import { qs, el } from '../utils/dom.js';

/**
 * Layer list UI — visibility, lock, selection, reorder, rename.
 */
export class LayerPanel {
  constructor(state, layerModel, mapCanvas) {
    this.state = state;
    this.layerModel = layerModel;
    this.mapCanvas = mapCanvas;
    this.listEl = qs('#layer-list');

    qs('#add-layer-btn').addEventListener('click', () => this.layerModel.addTileLayer());
    qs('#add-obj-layer-btn').addEventListener('click', () => this.layerModel.addObjectLayer());
    qs('#remove-layer-btn').addEventListener('click', () => {
      this.layerModel.removeLayer(this.state.get('activeLayerIndex'));
    });

    const dupBtn = qs('#duplicate-layer-btn');
    if (dupBtn) {
      dupBtn.addEventListener('click', () => {
        this.layerModel.duplicateLayer(this.state.get('activeLayerIndex'));
      });
    }

    this.state.onChange('layers', () => this.render());
    this.state.onChange('activeLayerIndex', () => this.render());

    this.render();
  }

  render() {
    const layers = this.state.get('layers');
    const activeIndex = this.state.get('activeLayerIndex');
    this.listEl.innerHTML = '';

    // Render in reverse order (top layer first in UI)
    for (let i = layers.length - 1; i >= 0; i--) {
      const layer = layers[i];
      const item = el('div', {
        className: `layer-item${i === activeIndex ? ' active' : ''}${layer.locked ? ' locked' : ''}`,
        draggable: 'true',
      });

      // Drag reordering
      item.dataset.layerIndex = i;
      item.addEventListener('dragstart', (e) => {
        e.dataTransfer.setData('text/plain', String(i));
        item.classList.add('dragging');
      });
      item.addEventListener('dragend', () => item.classList.remove('dragging'));
      item.addEventListener('dragover', (e) => {
        e.preventDefault();
        item.classList.add('drag-over');
      });
      item.addEventListener('dragleave', () => item.classList.remove('drag-over'));
      item.addEventListener('drop', (e) => {
        e.preventDefault();
        item.classList.remove('drag-over');
        const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
        const toIdx = i;
        if (fromIdx !== toIdx) {
          this.layerModel.moveLayer(fromIdx, toIdx);
          this.mapCanvas.invalidateAll();
        }
      });

      const visBtn = el('button', {
        className: `layer-vis${layer.visible ? ' on' : ''}`,
        title: 'Toggle visibility',
        onClick: (e) => {
          e.stopPropagation();
          this.layerModel.toggleVisibility(i);
          this.mapCanvas.invalidateLayer(i);
        },
      }, layer.visible ? '\u{1F441}' : '\u25CB');

      const lockBtn = el('button', {
        className: `layer-lock${layer.locked ? ' on' : ''}`,
        title: 'Toggle lock',
        onClick: (e) => {
          e.stopPropagation();
          this.layerModel.toggleLock(i);
        },
      }, layer.locked ? '\u{1F512}' : '\u{1F513}');

      const nameSpan = el('span', { className: 'layer-name' }, layer.name);
      nameSpan.addEventListener('dblclick', () => {
        const input = el('input', { type: 'text', value: layer.name, className: 'layer-rename' });
        nameSpan.replaceWith(input);
        input.focus();
        input.select();
        const finish = () => {
          this.layerModel.renameLayer(i, input.value || layer.name);
        };
        input.addEventListener('blur', finish);
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') input.blur();
          if (e.key === 'Escape') { input.value = layer.name; input.blur(); }
        });
      });

      const typeIcon = el('span', { className: 'layer-type' }, layer.type === 'objectgroup' ? 'OBJ' : 'TIL');

      item.addEventListener('click', () => this.state.set('activeLayerIndex', i));

      item.append(visBtn, lockBtn, nameSpan, typeIcon);

      // Opacity slider (shown only for active layer)
      if (i === activeIndex) {
        const opacityRow = el('div', { className: 'layer-opacity-row' });
        const slider = el('input', {
          type: 'range', min: '0', max: '100',
          value: String(Math.round((layer.opacity ?? 1) * 100)),
          className: 'layer-opacity-slider',
          onClick: (e) => e.stopPropagation(),
          onInput: (e) => {
            e.stopPropagation();
            this.layerModel.setOpacity(i, parseInt(e.target.value, 10) / 100);
            this.mapCanvas.invalidateLayer(i);
          },
        });
        const label = el('span', { className: 'layer-opacity-label' }, `${Math.round((layer.opacity ?? 1) * 100)}%`);
        opacityRow.append(slider, label);
        item.appendChild(opacityRow);
      }
      this.listEl.appendChild(item);
    }
  }
}
